import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';

import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexTitleSubtitle,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexStroke,
  ApexYAxis,
  ChartComponent,
} from 'ng-apexcharts';
import {
  areAllKeyValuesNull,
  buildGapBridgeSeries,
  randomNumber,
} from '../../utils';
import { IMetric } from '../../interfaces';
import { DatePipe } from '@angular/common';

// Series order is [gapA, gapB, data] so the data line renders on top. The gap
// bridge is split across two series so its dashed line never overlaps the solid
// data line (see buildGapBridgeSeries).
const DATA_SERIES_INDEX = 2;
const DATA_COLOR = '#2560B8';
const GAP_COLOR = '#D9214E';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
  tooltip: any;
  markers: any;
  legend: ApexLegend;
};

@Component({
  selector: 'app-sparkline-chart',
  templateUrl: './sparkline-chart.component.html',
  styleUrls: ['./sparkline-chart.component.scss'],
})
export class SparklineChartComponent implements OnInit {
  @ViewChild('chart', { static: true }) chart!: ChartComponent;
  @Input() name!: any;
  @Input() data: IMetric[] = [];

  public areAllKeyValuesNull = areAllKeyValuesNull;

  private _data = new Array(8);

  public chartOptions!: Partial<ChartOptions>;

  private datePipe = inject(DatePipe);

  constructor() {
    for (let i = 0; i < this._data.length; i++) {
      this._data[i] = randomNumber(-50, 50);
    }

    this.chartOptions = {
      series: [
        {
          name: '',
          data: [],
        },
      ],
      chart: {
        sparkline: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        height: 78,
        type: 'line',
        zoom: {
          enabled: false,
        },
      },
      markers: {
        colors: [GAP_COLOR, GAP_COLOR, DATA_COLOR],
        fillOpacity: [0, 0, 1],
        strokeOpacity: [0, 0, 0.9],
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: [2, 2, 2],
        curve: 'straight',
        colors: [GAP_COLOR, GAP_COLOR, DATA_COLOR],
        dashArray: [4, 4, 0],
      },
      legend: {
        show: false,
      },
      grid: {
        borderColor: '#E4ECEA',
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: false,
          },
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          top: -25,
          bottom: -23,
          left: 0,
          right: 0,
        },
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
    };
  }

  public ngOnInit(): void {
    for (let i = 0; i < 6; i++) {
      if (this.data[i] === undefined) {
        this.data[i] = { value: null, dateOfTaking: '' };
      }
    }
    const categories = this.data.map(item => item.dateOfTaking);
    const values = this.data.map(item => item.value);
    const tooltips = categories.map(el =>
      this.datePipe.transform(el, 'd.MM, h:mm a', 'UTC')
    );

    const [gapSeriesA, gapSeriesB] = buildGapBridgeSeries(values);

    this.chartOptions.series = [
      {
        name: this.name,
        data: gapSeriesA,
      },
      {
        name: this.name,
        data: gapSeriesB,
      },
      {
        name: this.name,
        data: values,
      },
    ];

    this.chartOptions.xaxis = {
      labels: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      crosshairs: {
        show: true,
      },
      tooltip: {
        enabled: false,
      },
      categories: categories,
    };

    this.chartOptions.tooltip = {
      style: {
        fontSize: '12px',
        fontFamily: "'Poppins', sans-serif",
      },
      custom: function ({ series, dataPointIndex, w }: any) {
        return `
              <div class="ml-tooltip">
                <div class="ml-tooltip__head">
                ${tooltips?.[dataPointIndex] ? tooltips?.[dataPointIndex] : '-'}
                </div>
                <div class="ml-tooltip__body">
                  <div class="ml-tooltip__point"></div>
                  <span class="ml-tooltip__name">${
                    w.globals.seriesNames[DATA_SERIES_INDEX]
                  }</span>
                  <strong class="ml-tooltip__value">${
                    series[DATA_SERIES_INDEX][dataPointIndex]
                      ? series[DATA_SERIES_INDEX][dataPointIndex]
                      : '-'
                  }</strong>
                </div>
              </div>
            `;
      },
      marker: {
        show: false,
      },
    };
  }
}
